import type { PlanDays } from "@/helpers/plansToPacks";
import type { PawsExperiments } from "./types";

/**
 * Code-defined quiz experiment: remove the 7-day plan (paws-on-plates#125).
 * paws-admin type `funnel_behaviour`, target `quiz.flow`.
 *
 *   a (control) — today's 7 / 14 / 28-day ladder, 7-day pre-selected.
 *   b           — 7-day removed, 14-day pre-selected and badged "Most popular",
 *                 plus the reassurance strip, freezer line, inline CTA and
 *                 Clean Bowl Promise module (PlanLadderExtras.tsx).
 *
 * Any variant other than TWO_PLAN_VARIANT (absent experiment, paused,
 * bootstrap failure) renders control — fail open.
 *
 * Exposure fires at quiz start (QuizExposure in apps/quizpage.tsx), so the
 * experiment's conversion rate is purchases per quiz start, as the test
 * brief specifies.
 */
export const PLAN_LADDER_EXPERIMENT_KEY = "quiz-plan-test";
export const TWO_PLAN_VARIANT = "b";

/** Plan lengths offered on the plan step for the given arm. */
export function planDaysForArm(twoPlan: boolean): PlanDays[] {
    return twoPlan ? [14, 28] : [7, 14, 28];
}

/** Plan pre-selected on first view. */
export function defaultPlanDaysForArm(twoPlan: boolean): PlanDays {
    return twoPlan ? 14 : 7;
}

/**
 * Plan-test tagging (#125 item 14). The arm string is the join key used
 * IDENTICALLY on the Shopify order + customer tag, the Recharge subscription
 * + customer, and the Klaviyo profile — never change one side alone
 * (paws-admin: internal/experiments/plantest.go).
 */
export const PLAN_TEST_ARMS: Readonly<Record<string, string>> = {
    a: "quiz_plan_test_a",
    b: "quiz_plan_test_b",
};

/** Property / attribute names carrying the tags. Leading underscore hides them from the customer. */
export const PLAN_TEST_PROPS = {
    arm: "_quiz_plan_test",
    cadence: "_signup_cadence",
    assignedAt: "_quiz_plan_test_assigned_at",
} as const;

export type PlanTestTag = { arm: string; assignedAt: string };

/**
 * The visitor's plan-test tag, or null when there is nothing to record: no
 * bootstrap, experiment not running, unknown variant, or a QA override (QA
 * previews must never land in the cohort). assignedAt is the UTC ISO date
 * (YYYY-MM-DD) of first assignment.
 */
export function planTestTag(
    store: Readonly<PawsExperiments> | null | undefined
): PlanTestTag | null {
    const rec = store?.assignments?.[PLAN_LADDER_EXPERIMENT_KEY];
    if (!rec || rec.src === "qa_override") return null;
    const arm = PLAN_TEST_ARMS[rec.variant];
    if (!arm) return null;
    const at = new Date(Number(rec.at));
    if (Number.isNaN(at.getTime())) return null;
    return { arm, assignedAt: at.toISOString().slice(0, 10) };
}

/** Line-item / cart-attribute properties for a tagged visitor (empty object otherwise). */
export function planTestProperties(
    tag: PlanTestTag | null,
    planDays: number
): Record<string, string> {
    if (!tag) return {};
    return {
        [PLAN_TEST_PROPS.arm]: tag.arm,
        [PLAN_TEST_PROPS.assignedAt]: tag.assignedAt,
        ...(planDays > 0 && { [PLAN_TEST_PROPS.cadence]: String(planDays) }),
    };
}
